import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Checkbox } from '../Checkbox';
import { DateRangePicker } from '../DateRangePicker';
import { Input, InputType } from '../Input';
import { Radio } from '../Radio';
import { HeroIcons } from '../style/heroicons';
import { Text } from '../Text';
import { Side } from '../types';
import { appendClassProps } from '../util';
import { useMounted, useOutsideAlerter } from '../util/hooks';
import { Mode, Position } from './index.types';
// Component for conditionally forwarding ref
const UseOverrideRef = ({ forwardedRef, dropdownRef }) => {
    useImperativeHandle(forwardedRef, () => {
        return {
            ...dropdownRef?.current,
            focus: () => {
                dropdownRef?.current?.focus();
            }
        };
    });
    return null;
};
const sortMenuItems = ({ menuItems, mode }) => {
    return menuItems
        ?.sort((a, b) => {
        if (a.label < b.label) {
            return -1;
        }
        if (a.label > b.label) {
            return 1;
        }
        return 0;
    })
        .sort((a, b) => {
        if (mode !== Mode.single) {
            if (a.selected && !b.selected)
                return -1;
            if (!a.selected && b.selected)
                return 1;
        }
        return 0;
    });
};
/**
 - Use text for any text on the screen to conform to typography styles
 */
const Menu = ({ closeOnSelect = false, openOnMount = false, 
// side = Side.left,
fullWidth = false, searchable = true, mode = Mode.multi, disabled = false, show = true, selectAll = true, loading = false, 
// top,
children, className, menuItems, dateRange, childRef, position, yOffset, onChange, onHide, onOpen }, ref) => {
    const [showMenu, setShowMenu] = useState((openOnMount && show) ?? undefined);
    const [searchTerm, setSearchTerm] = useState('');
    const [newDateRange, setNewDateRange] = useState(dateRange);
    const [newMenuItems, setNewMenuItems] = useState(menuItems);
    const [min, setMin] = useState();
    const [max, setMax] = useState();
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);
    const mounted = useMounted();
    useEffect(() => {
        // only open the first time the menu is rendered (when it mounts)
        if (openOnMount && !mounted && show) {
            setShowMenu(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openOnMount, show]);
    useEffect(() => {
        setNewMenuItems(sortMenuItems({ menuItems, mode }));
    }, [menuItems, mode]);
    useEffect(() => {
        setNewDateRange(dateRange);
    }, [dateRange]);
    useEffect(() => {
        if (showMenu === false) {
            setSearchTerm('');
            const sortedMenuItems = sortMenuItems({ menuItems: newMenuItems, mode });
            if (sortedMenuItems)
                handleChangeMenuItems(sortedMenuItems);
            if (onHide) {
                onHide();
            }
            if (mounted) {
                if (mode === Mode.dateRange) {
                    onChange(newDateRange);
                }
                else {
                    onChange(newMenuItems);
                }
            }
        }
        else if (onOpen) {
            onOpen();
        }
        if (showMenu && childRef?.current && dropdownRef?.current) {
            // determine the size of the dropdown
            const { top: Ym, left: Xm, bottom, right } = dropdownRef.current.getBoundingClientRect();
            // calculate the position in the window, to adjust the direction
            const { top: Yc, left: Xc, bottom: childBottom, right: childRight } = childRef.current.getBoundingClientRect();
            const parentRect = {
                bottom: window.innerHeight,
                right: window.innerWidth
            };
            const { bottom: parentBottom, right: parentRight } = parentRect
                ? parentRect
                : { bottom: null, right: null };
            const Hc = childBottom - Yc;
            const Wc = childRight - Xc;
            const Hm = bottom - Ym;
            const Wm = right - Xm;
            let xPosition = 0;
            let yPosition = 0 + Hc + (yOffset || 0);
            const yOverflow = parentBottom ? Yc + Hm > parentBottom : false;
            const xOverflow = parentRight ? Xc + Wm > parentRight : false;
            // overrides
            if (position === Position.bottomLeft) {
                xPosition = -(Wm - Wc);
            }
            else if (position === Position.topLeft) {
                xPosition = -(Wm - Wc);
                yPosition = -Hm;
            }
            else if (position === Position.topRight) {
                yPosition = -Hm;
            }
            else if (position !== Position.bottomRight) {
                if (xOverflow)
                    xPosition = -(Wm - Wc);
                if (yOverflow)
                    yPosition = -Hm;
            }
            // adjust the position of the dropdown
            if (dropdownRef?.current) {
                dropdownRef.current.style['top'] = `${yPosition}px`;
                dropdownRef.current.style['left'] = `${xPosition}px`;
                dropdownRef.current.style['minWidth'] = `${Wc}px`;
            }
        }
    }, [showMenu, position, yOffset]);
    useEffect(() => {
        const newMenuItems = cloneDeep(menuItems)?.map((item) => {
            if (min !== undefined) {
                if (parseFloat(item.id) >= min) {
                    if (max !== undefined && parseFloat(item.id) > max) {
                        item.selected = false;
                    }
                    else {
                        item.selected = true;
                    }
                }
                else {
                    item.selected = false;
                }
            }
            if (max !== undefined) {
                if (parseFloat(item.id) <= max) {
                    if (min !== undefined && parseFloat(item.id) < min) {
                        item.selected = false;
                    }
                    else {
                        item.selected = true;
                    }
                }
                else {
                    item.selected = false;
                }
            }
            return item;
        });
        if (!isEqual(menuItems, newMenuItems))
            setNewMenuItems(newMenuItems);
    }, [min, max]);
    const handleClickWrapper = () => {
        if (showMenu) {
            setShowMenu(false);
        }
    };
    const handleClickMenuButton = () => {
        if (!disabled)
            setShowMenu(!showMenu);
    };
    const handleChangeSearchTerm = (term) => {
        setSearchTerm(term);
    };
    const handleChangeDateRange = (newRange) => {
        setNewDateRange(newRange);
    };
    const handleChangeMenuItems = (newItems) => {
        setNewMenuItems(newItems);
    };
    const handleClearAll = () => {
        const newMenuItems = menuItems?.map((item) => {
            item.selected = false;
            return item;
        });
        setMax(undefined);
        setMin(undefined);
        setNewMenuItems(newMenuItems);
    };
    const handleSelectAll = () => {
        const newMenuItems = menuItems?.map((item) => {
            item.selected = true;
            return item;
        });
        setMax(undefined);
        setMin(undefined);
        setNewMenuItems(newMenuItems);
    };
    const handleSelectItem = (id, selected) => {
        let newItems;
        if (mode === Mode.single) {
            newItems = cloneDeep(newMenuItems)?.map((item) => {
                if (item.id === id) {
                    item.selected = true;
                }
                else {
                    item.selected = false;
                }
                return item;
            });
        }
        else {
            newItems = cloneDeep(newMenuItems)?.map((item) => {
                if (item.id === id)
                    item.selected = !selected;
                return item;
            });
        }
        if (newItems)
            handleChangeMenuItems(newItems);
    };
    const allNumberedItems = useMemo(() => {
        return ((menuItems?.length ?? 0) > 0 &&
            menuItems?.every((item) => typeof item.id === 'number' && !isNaN(item.id)));
    }, [menuItems]);
    useOutsideAlerter(wrapperRef, handleClickWrapper);
    let content;
    if (mode === Mode.dateRange && dateRange) {
        content = (React.createElement(DateRangePicker, { className: "flex flex-col gap-2", dateRange: dateRange, onChange: handleChangeDateRange, side: Side.left, clearable: true }));
    }
    else {
        content = (React.createElement(React.Fragment, null,
            searchable && mode !== Mode.range && (React.createElement(Input, { hideErrorSection: true, leftIcon: HeroIcons.SearchIcon, value: searchTerm || '', onChangeValue: handleChangeSearchTerm })),
            mode !== Mode.range && !loading && mode === Mode.multi && selectAll && (React.createElement("div", { "data-testid": '(de)select all', key: '(de)select all', className: `hover:text-blue-600 flex flex-row gap-2 items-center cursor-pointer` },
                React.createElement("div", { className: "flex flex-row w-full justify-between" },
                    React.createElement("span", { className: "cursor-pointer", onClick: () => {
                            if (menuItems?.every((item) => item.selected)) {
                                handleClearAll();
                            }
                            else {
                                handleSelectAll();
                            }
                        } }, menuItems?.every((item) => item.selected)
                        ? 'clear all'
                        : 'select all'),
                    React.createElement("span", null,
                        newMenuItems?.filter((item) => item.selected).length ?? 0,
                        "/",
                        newMenuItems?.length ?? 0)))),
            mode !== Mode.range && !loading && (React.createElement("ul", { className: `flex flex-col overflow-y-auto` }, newMenuItems
                ?.filter((item) => {
                if (!searchTerm)
                    return true;
                return item.label
                    .toString()
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            })
                ?.map((menuItem, i) => (React.createElement("li", { "data-testid": i, key: menuItem.key || menuItem.id, className: `hover:text-blue-600 flex flex-row gap-2 items-center ${mode !== Mode.single ? 'cursor-pointer' : ''}`, onClick: () => {
                    handleSelectItem(menuItem.id, menuItem.selected || false);
                    if (closeOnSelect)
                        setShowMenu(false);
                    setMin(undefined);
                    setMax(undefined);
                } },
                mode === Mode.multi && (
                // TODO: detect shift click to select more than 1 at a time
                React.createElement(React.Fragment, null,
                    React.createElement(Checkbox, { hideErrorSection: true, checked: menuItem.selected ?? false, onChangeValue: () => {
                            handleSelectItem(menuItem.id, menuItem.selected || false);
                            if (closeOnSelect)
                                setShowMenu(false);
                            setMin(undefined);
                            setMax(undefined);
                        } }),
                    menuItem.label)),
                mode === Mode.single && !closeOnSelect && (React.createElement(Radio, { label: menuItem.label.toString(), selected: menuItem.selected ?? false, onSelect: () => {
                        handleSelectItem(menuItem.id, menuItem.selected ?? false);
                    } })),
                mode === Mode.single && closeOnSelect && (React.createElement("div", { className: "cursor-pointer hover:opacity-80", onClick: () => handleSelectItem(menuItem.id, menuItem.selected ?? false) },
                    React.createElement(Text, null, menuItem.label.toString())))))))),
            allNumberedItems && (React.createElement("div", { className: "flex flex-row gap-2" },
                React.createElement(Input, { hideErrorSection: true, type: InputType.number, onChangeValue: (value) => {
                        setMin(value);
                    }, placeholder: Math.min(...menuItems?.map((item) => item.id)).toString() }),
                React.createElement(Text, { inline: true }, "-"),
                React.createElement(Input, { hideErrorSection: true, type: InputType.number, onChangeValue: (value) => {
                        setMax(value);
                    }, placeholder: Math.max(...menuItems?.map((item) => item.id)).toString() })))));
    }
    return (React.createElement("div", { "data-testid": "menu", className: `${!!show ? '' : ' hidden'}${appendClassProps(className)}`, ref: wrapperRef },
        ref && Object.keys(ref).length > 0 ? (React.createElement(UseOverrideRef, { forwardedRef: ref, dropdownRef: dropdownRef })) : null,
        children &&
            React.cloneElement(children, { onClick: handleClickMenuButton }),
        React.createElement("div", { ref: dropdownRef, className: `shadow-md bg-gray-50 text-blue-800 flex flex-col gap-2 p-2 absolute rounded-tl-sm rounded-b-sm ${fullWidth ? 'w-full' : 'min-w-[14rem]'} max-h-64${!!showMenu ? '' : ' hidden'} z-900` },
            content,
            loading && React.createElement(Skeleton, { className: "w-full", count: 5 }))));
};
const WrappedMenu = forwardRef(Menu);
export * from './index.types';
export { WrappedMenu as Menu, Menu as BareMenu }; // for storybook (fails to properly parse component if wrapped with forwardRef)
//# sourceMappingURL=index.js.map